import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Calendar, Clock, Users, Video, MapPin, Plus, Search, Filter,
  Check, XCircle, AlertTriangle, Settings, BarChart3,
  CalendarDays, User, Briefcase, MessageCircle
} from 'lucide-react';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const InterviewScheduling = () => {
  const [activeTab, setActiveTab] = useState('schedule');
  const [interviews, setInterviews] = useState([]);
  const [calendarAccounts, setCalendarAccounts] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [interviewMetrics, setInterviewMetrics] = useState({});
  const [showNewInterviewModal, setShowNewInterviewModal] = useState(false);
  const [showCalendarModal, setShowCalendarModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  // Mock current user and tenant data
  const currentUser = {
    id: 'user123',
    name: 'Sarah Johnson',
    email: 'sarah@company.com',
    tenant_id: 'tenant1'
  };

  useEffect(() => {
    fetchInterviews();
    fetchCalendarAccounts();
    fetchInterviewMetrics();
  }, []);

  const fetchInterviews = async () => {
    try {
      const response = await axios.get(`${API}/api/interviews/${currentUser.tenant_id}`);
      setInterviews(response.data.interviews || []);
    } catch (error) {
      console.error('Error fetching interviews:', error);
      // Mock data for development
      setInterviews([
        {
          id: '1',
          title: 'Technical Interview - John Doe (Software Engineer)',
          job_id: 'job1',
          candidate_id: 'candidate1',
          interviewer_id: 'interviewer1',
          interview_type: 'technical',
          status: 'scheduled',
          start_time: new Date(Date.now() + 86400000).toISOString(), // Tomorrow
          end_time: new Date(Date.now() + 86400000 + 3600000).toISOString(), // Tomorrow + 1 hour
          location: 'Conference Room A',
          meeting_url: 'https://meet.google.com/abc-def-ghi',
          attendee_emails: ['john.doe@email.com', 'sarah@company.com'],
          candidate_name: 'John Doe',
          job_title: 'Software Engineer'
        },
        {
          id: '2',
          title: 'Screening Call - Jane Smith (Product Manager)',
          job_id: 'job2',
          candidate_id: 'candidate2',
          interviewer_id: 'interviewer2',
          interview_type: 'screening',
          status: 'completed',
          start_time: new Date(Date.now() - 86400000).toISOString(), // Yesterday
          end_time: new Date(Date.now() - 86400000 + 1800000).toISOString(), // Yesterday + 30 min
          location: 'Phone',
          candidate_name: 'Jane Smith',
          job_title: 'Product Manager'
        }
      ]);
    }
  };

  const fetchCalendarAccounts = async () => {
    try {
      const response = await axios.get(`${API}/api/interviews/calendar/accounts/${currentUser.tenant_id}?user_id=${currentUser.id}`);
      setCalendarAccounts(response.data.accounts || []);
    } catch (error) {
      console.error('Error fetching calendar accounts:', error);
      // Mock data for development
      setCalendarAccounts([
        {
          id: '1',
          email: 'sarah@company.com',
          provider: 'emergent_gmail',
          is_primary: true,
          sync_enabled: true
        }
      ]);
    }
  };

  const fetchInterviewMetrics = async () => {
    try {
      const response = await axios.get(`${API}/api/interviews/metrics/${currentUser.tenant_id}`);
      setInterviewMetrics(response.data.metrics || {});
    } catch (error) {
      console.error('Error fetching interview metrics:', error);
      // Mock metrics for development
      setInterviewMetrics({
        total_interviews_scheduled: 45,
        completed_interviews: 38,
        cancelled_interviews: 5,
        no_show_interviews: 2,
        completion_rate: 84.4,
        average_scheduling_lead_time_hours: 72,
        popular_time_slots: [
          { time: '10:00', count: 12 },
          { time: '14:00', count: 10 },
          { time: '11:00', count: 8 }
        ],
        interviewer_utilization: {
          interviewer_1: { scheduled: 15, completed: 13 },
          interviewer_2: { scheduled: 12, completed: 10 },
          interviewer_3: { scheduled: 18, completed: 15 }
        }
      });
    }
  };

  const findAvailableSlots = async (searchParams) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/api/interviews/slots/find`, {
        tenant_id: currentUser.tenant_id,
        interviewer_ids: searchParams.interviewer_ids || ['interviewer1', 'interviewer2'],
        start_date: searchParams.start_date || new Date().toISOString(),
        end_date: searchParams.end_date || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        duration_minutes: searchParams.duration_minutes || 60,
        buffer_minutes: 15
      });
      
      setAvailableSlots(response.data.optimal_slots || []);
      toast.success(`Found ${response.data.total_slots} available time slots`);
    } catch (error) {
      console.error('Error finding available slots:', error);
      toast.error('Failed to find available slots');
      
      // Mock slots for development
      setAvailableSlots([
        {
          start_time: new Date(Date.now() + 86400000).toISOString(),
          end_time: new Date(Date.now() + 86400000 + 3600000).toISOString(),
          available_interviewers: [
            { id: 'interviewer1', name: 'Sarah Johnson', email: 'sarah@company.com' }
          ],
          score: 10
        },
        {
          start_time: new Date(Date.now() + 2 * 86400000).toISOString(),
          end_time: new Date(Date.now() + 2 * 86400000 + 3600000).toISOString(),
          available_interviewers: [
            { id: 'interviewer2', name: 'Mike Chen', email: 'mike@company.com' }
          ],
          score: 8
        }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const connectCalendar = async (provider) => {
    try {
      if (provider === 'google' || provider === 'emergent_gmail') {
        // Use Emergent OAuth for Google Calendar
        const redirectUrl = `${window.location.origin}/interviews`;
        const authUrl = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}&provider=calendar`;
        window.location.href = authUrl;
      } else {
        toast.info('Outlook calendar integration coming soon!');
      }
    } catch (error) {
      console.error('Error connecting calendar:', error);
      toast.error('Failed to connect calendar');
    }
  };

  const scheduleInterview = async (interviewData) => {
    try {
      setLoading(true);
      const response = await axios.post(`${API}/api/interviews`, {
        tenant_id: currentUser.tenant_id,
        ...interviewData,
        created_by: currentUser.id
      });
      
      if (response.data.success) {
        toast.success('Interview scheduled successfully!');
        setShowNewInterviewModal(false);
        fetchInterviews();
        
        if (response.data.meeting_url) {
          toast.success(`Meeting link created: ${response.data.meeting_url}`);
        }
      }
    } catch (error) {
      console.error('Error scheduling interview:', error);
      toast.error('Failed to schedule interview');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'scheduled': return 'text-blue-600 bg-blue-100';
      case 'confirmed': return 'text-green-600 bg-green-100';
      case 'completed': return 'text-gray-600 bg-gray-100';
      case 'cancelled': return 'text-red-600 bg-red-100';
      case 'no_show': return 'text-orange-600 bg-orange-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getInterviewTypeIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="h-4 w-4" />;
      case 'phone': return <MessageCircle className="h-4 w-4" />;
      case 'in_person': return <MapPin className="h-4 w-4" />;
      default: return <Calendar className="h-4 w-4" />;
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-md p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Calendar className="h-8 w-8 text-purple-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Interview Scheduling</h1>
              <p className="text-gray-600">Manage interviews and calendar integration</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <button
              onClick={() => findAvailableSlots({})}
              className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Search className="h-4 w-4" />
              <span>Find Slots</span>
            </button>
            
            <button
              onClick={() => setShowNewInterviewModal(true)}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <Plus className="h-4 w-4" />
              <span>Schedule Interview</span>
            </button>
            
            <button
              onClick={() => setShowCalendarModal(true)}
              className="flex items-center space-x-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <Settings className="h-4 w-4" />
              <span>Calendar Settings</span>
            </button>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white rounded-xl shadow-md mb-6">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {[
              { id: 'schedule', label: 'Schedule', icon: CalendarDays },
              { id: 'interviews', label: 'Interviews', icon: Users },
              { id: 'availability', label: 'Availability', icon: Clock },
              { id: 'metrics', label: 'Analytics', icon: BarChart3 }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center space-x-2 py-4 px-2 border-b-2 font-medium text-sm ${
                  activeTab === tab.id
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <tab.icon className="h-4 w-4" />
                <span>{tab.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Schedule Tab */}
        {activeTab === 'schedule' && (
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Available Slots */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Available Time Slots</h3>
                {loading ? (
                  <div className="text-center py-8">
                    <Clock className="h-8 w-8 text-gray-400 mx-auto mb-3 animate-spin" />
                    <p className="text-gray-600">Finding available slots...</p>
                  </div>
                ) : availableSlots.length > 0 ? (
                  <div className="space-y-3 max-h-96 overflow-y-auto">
                    {availableSlots.map((slot, index) => (
                      <div key={index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <Calendar className="h-4 w-4 text-purple-600" />
                            <span className="font-medium">
                              {new Date(slot.start_time).toLocaleDateString()}
                            </span>
                          </div>
                          <span className="text-sm text-gray-500">Score: {slot.score}/10</span>
                        </div>
                        
                        <div className="flex items-center space-x-4 text-sm text-gray-600 mb-3">
                          <div className="flex items-center space-x-1">
                            <Clock className="h-3 w-3" />
                            <span>
                              {new Date(slot.start_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - 
                              {new Date(slot.end_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                            </span>
                          </div>
                          <div className="flex items-center space-x-1">
                            <Users className="h-3 w-3" />
                            <span>{slot.available_interviewers?.length || 0} interviewers</span>
                          </div>
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="text-sm">
                            {slot.available_interviewers?.map((interviewer, idx) => (
                              <span key={idx} className="inline-block bg-purple-100 text-purple-800 px-2 py-1 rounded mr-2">
                                {interviewer.name}
                              </span>
                            ))}
                          </div>
                          <button 
                            onClick={() => {
                              setSelectedCandidate({ name: 'Selected Candidate' });
                              setSelectedJob({ title: 'Selected Position' });
                              // Pre-fill modal with slot data
                              setShowNewInterviewModal(true);
                            }}
                            className="text-sm bg-purple-600 text-white px-3 py-1 rounded hover:bg-purple-700"
                          >
                            Book Slot
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <CalendarDays className="h-12 w-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600 mb-4">No available slots found</p>
                    <button 
                      onClick={() => findAvailableSlots({})}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                    >
                      Search for Slots
                    </button>
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
                <div className="space-y-4">
                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Calendar Status</h4>
                    <div className="space-y-2">
                      {calendarAccounts.length > 0 ? (
                        calendarAccounts.map((account, index) => (
                          <div key={index} className="flex items-center justify-between">
                            <div className="flex items-center space-x-2">
                              <Check className="h-4 w-4 text-green-500" />
                              <span className="text-sm">{account.email}</span>
                              <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                                {account.provider}
                              </span>
                            </div>
                            {account.is_primary && (
                              <span className="text-xs text-gray-500">Primary</span>
                            )}
                          </div>
                        ))
                      ) : (
                        <div className="text-center py-4">
                          <XCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
                          <p className="text-sm text-gray-600 mb-3">No calendar connected</p>
                          <button 
                            onClick={() => connectCalendar('emergent_gmail')}
                            className="text-sm bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                          >
                            Connect Calendar
                          </button>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-lg p-4">
                    <h4 className="font-medium text-gray-900 mb-2">Search Parameters</h4>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                        <select className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                          <option value="30">30 minutes</option>
                          <option value="60" selected>1 hour</option>
                          <option value="90">1.5 hours</option>
                          <option value="120">2 hours</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date Range</label>
                        <div className="flex space-x-2">
                          <input 
                            type="date" 
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            defaultValue={new Date().toISOString().split('T')[0]}
                          />
                          <input 
                            type="date" 
                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                            defaultValue={new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]}
                          />
                        </div>
                      </div>
                      <button 
                        onClick={() => findAvailableSlots({})}
                        className="w-full px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                      >
                        Update Search
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Interviews Tab */}
        {activeTab === 'interviews' && (
          <div className="p-6">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900">Scheduled Interviews</h3>
              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <Filter className="h-4 w-4 text-gray-400" />
                  <select className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="">All Status</option>
                    <option value="scheduled">Scheduled</option>
                    <option value="confirmed">Confirmed</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {interviews.map((interview) => (
                <div key={interview.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center space-x-2">
                      {getInterviewTypeIcon(interview.interview_type)}
                      <span className="font-medium text-gray-900 text-sm">
                        {interview.interview_type?.toUpperCase()}
                      </span>
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(interview.status)}`}>
                      {interview.status?.toUpperCase()}
                    </span>
                  </div>

                  <h4 className="font-semibold text-gray-900 mb-2 line-clamp-2">
                    {interview.candidate_name || 'Candidate Name'}
                  </h4>
                  
                  <p className="text-sm text-gray-600 mb-3">
                    {interview.job_title || 'Position Title'}
                  </p>

                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-3 w-3" />
                      <span>{new Date(interview.start_time).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Clock className="h-3 w-3" />
                      <span>
                        {new Date(interview.start_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})} - 
                        {new Date(interview.end_time).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                      </span>
                    </div>
                    {interview.location && (
                      <div className="flex items-center space-x-2">
                        <MapPin className="h-3 w-3" />
                        <span className="truncate">{interview.location}</span>
                      </div>
                    )}
                  </div>

                  <div className="mt-4 flex space-x-2">
                    <button className="flex-1 px-3 py-2 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200">
                      Details
                    </button>
                    {interview.meeting_url && (
                      <button 
                        onClick={() => window.open(interview.meeting_url, '_blank')}
                        className="flex-1 px-3 py-2 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                      >
                        Join Meeting
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {interviews.length === 0 && (
              <div className="text-center py-12">
                <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No Interviews Scheduled</h3>
                <p className="text-gray-600 mb-6">Start by scheduling your first interview</p>
                <button 
                  onClick={() => setShowNewInterviewModal(true)}
                  className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Schedule Interview
                </button>
              </div>
            )}
          </div>
        )}

        {/* Metrics Tab */}
        {activeTab === 'metrics' && (
          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-600 text-sm font-medium">Total Interviews</p>
                    <p className="text-2xl font-bold text-blue-900">{interviewMetrics.total_interviews_scheduled || 0}</p>
                  </div>
                  <Calendar className="h-8 w-8 text-blue-600" />
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-green-600 text-sm font-medium">Completion Rate</p>
                    <p className="text-2xl font-bold text-green-900">{interviewMetrics.completion_rate || 0}%</p>
                  </div>
                  <Check className="h-8 w-8 text-green-600" />
                </div>
              </div>

              <div className="bg-orange-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-orange-600 text-sm font-medium">Avg Lead Time</p>
                    <p className="text-2xl font-bold text-orange-900">{interviewMetrics.average_scheduling_lead_time_hours || 0}h</p>
                  </div>
                  <Clock className="h-8 w-8 text-orange-600" />
                </div>
              </div>

              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-purple-600 text-sm font-medium">No Shows</p>
                    <p className="text-2xl font-bold text-purple-900">{interviewMetrics.no_show_interviews || 0}</p>
                  </div>
                  <AlertTriangle className="h-8 w-8 text-purple-600" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Popular Time Slots</h4>
                <div className="space-y-2">
                  {interviewMetrics.popular_time_slots?.map((slot, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{slot.time}</span>
                      <span className="text-sm font-medium text-gray-900">{slot.count} interviews</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="font-semibold text-gray-900 mb-3">Interviewer Utilization</h4>
                <div className="space-y-2">
                  {Object.entries(interviewMetrics.interviewer_utilization || {}).map(([interviewer, stats]) => (
                    <div key={interviewer} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{interviewer.replace('_', ' ')}</span>
                      <span className="text-sm font-medium text-gray-900">
                        {stats.completed}/{stats.scheduled}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Modals would go here - for Phase 2A we'll keep them simple */}
      {showCalendarModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold mb-4">Calendar Settings</h3>
            <div className="space-y-4">
              <button 
                onClick={() => connectCalendar('emergent_gmail')}
                className="w-full px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center space-x-2"
              >
                <span>Connect Google Calendar</span>
              </button>
              <button 
                onClick={() => connectCalendar('outlook')}
                className="w-full px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center space-x-2"
              >
                <span>Connect Outlook Calendar</span>
              </button>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button 
                onClick={() => setShowCalendarModal(false)}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterviewScheduling;